<template>
  <div id="contract-editor-loan-form" class="box">
    <b-container>
      <h2>{{ $t(renew ? 'RENEWAL_INFO' : 'LOAN_INFO') }}</h2>

      <div class="checkbox-wrapper" v-if="!locked">
        <input type="checkbox" :checked="canEditMaturityDate" @change="toggleCanEditMaturityDate()" />
        <label class="label">{{ $t('EDIT_MATURITY_DATE') }}</label>
      </div>

      <div class="checkbox-wrapper" v-if="!!renew">
        <input type="checkbox" :checked="canEditOverdueFee" @change="toggleCanEditOverdueFee()" />
        <label class="label">{{ $t('EDIT_OVERDUE_FEE') }}</label>
      </div>

      <b-row>
        <b-col xs="12" md="6" lg="4" class="input-wrapper">
          <select-input
            :label="$t('LOAN_PRODUCT')"
            v-model="product"
            :items="products"
            borderbottom
            required
            ref="productInput"
            :disabled="locked"
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper">
          <text-input
            :label="$t('INTEREST_RATE')"
            v-model="interestRate"
            borderbottom
            :disabled="locked || !product_ || (product_ && (product_.minInterestRate && (product_.minInterestRate === product_.maxInterestRate)))"
            required
            ref="interestRateInput"
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper">
          <text-input
            :label="$t('APPROVED_AMOUNT')"
            v-model="approvedAmount"
            :hint="changeDigitToViText(approvedAmount)"
            borderbottom
            required
            ref="approvedAmountInput"
            :validate="validateApprovedAmount"
            :disabled="locked"
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper">
          <date-input
            v-model="disbursedOn"
            :label="$t(!!renew ? 'RENEWAL_DATE' : 'DISBURSEMENT_DATE')"
            required
            borderbottom
            ref="disbursedOnInput"
            :validate="validateDisbursementDate"
            :disabled="!!renew || locked"
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper">
          <text-input
            :label="$t('PRINCIPAL')"
            v-model="principalAmount"
            :hint="changeDigitToViText(principalAmount)"
            borderbottom
            disabled
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper">
          <text-input
            :label="$t('INTEREST')"
            v-model="interestAmount"
            :hint="changeDigitToViText(interestAmount)"
            borderbottom
            disabled
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper">
          <date-input
            v-model="maturesOn"
            :label="$t('MATURITY_DATE')"
            required
            borderbottom
            :disabled="!canEditMaturityDate"
            :validate="validateMaturityDate"
            ref="maturesOnInput"
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper">
          <text-input
            :label="$t('ROUNDING_FEE')"
            v-model="roundingFee"
            :hint="changeDigitToViText(roundingFee)"
            borderbottom
            disabled
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper">
          <text-input
            :label="$t('STORAGE_FEE')"
            v-model="storageFee"
            :hint="changeDigitToViText(storageFee)"
            borderbottom
            :disabled="locked"
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper" v-show="!renew">
          <text-input
            :label="$t('DISBURSEMENT_AMOUNT')"
            v-model="disbursementAmount"
            :hint="changeDigitToViText(disbursementAmount)"
            borderbottom
            disabled
            :validate="validateDisbursementAmount"
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper" v-if="!!renew && (earlyRenewalCumulativeDays > 0)">
          <text-input
            :label="$t('EARLY_RENEWAL_CUMULATIVE_DAYS')"
            :value="earlyRenewalCumulativeDays"
            borderbottom
            disabled
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper" v-if="!!renew && (earlyRenewalCumulativeDays <= 0)">
          <text-input
            :label="$t('NUMBER_OF_LATE_DAYS')"
            :value="numberOfLateDays"
            borderbottom
            disabled
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper" v-if="!!renew">
          <text-input
            :label="$t('OVERDUE_FEE')"
            v-model="overdueFeeSwap"
            :hint="changeDigitToViText(overdueFeeSwap)"
            borderbottom
            :disabled="!canEditOverdueFee"
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper" v-if="!!renew">
          <text-input
            :label="$t((formerContract.renewalDifference <= 0) ? 'OLD_CNTRCT_PENALTY' : 'OLD_CNTRCT_REFUND')"
            :value="(Math.abs(formerContract.renewalDifference) / MILLION).toFixed(3)"
            :hint="changeDigitToViText((Math.abs(formerContract.renewalDifference) / MILLION).toFixed(3))"
            borderbottom
            disabled
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper" v-if="!!renew">
          <text-input
            :label="$t('WAIVED_AMOUNT')"
            :value="(formerContract.waivedAmount / MILLION)"
            :hint="changeDigitToViText(formerContract.waivedAmount / MILLION)"
            borderbottom
            disabled
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper" v-show="!!renew">
          <text-input
            :label="$t((renewalAmount >= 0) ? 'RENEWAL_AMOUNT' : 'DISBURSEMENT_AMOUNT')"
            :value="(Math.abs(renewalAmount) / MILLION).toFixed(3)"
            :hint="changeDigitToViText((Math.abs(renewalAmount) / MILLION).toFixed(3))"
            borderbottom
            disabled
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper" v-if="!!renew" v-show="renewalAmount >= 0">
          <text-input
            :label="$t('RECEIVED_AMOUNT_FROM_CLIENT')"
            :value="receivedAmount"
            :hint="changeDigitToViText(receivedAmount)"
            borderbottom
            required
            :validate="validateRepaidAmount"
            ref="receivedAmountInput"
            disabled
          />
        </b-col>

        <b-col xs="12" md="6" lg="4" class="input-wrapper" v-if="!!renew && (renewalDifference !== 0)" v-show="renewalAmount >= 0">
          <text-input
            :label="$t((renewalDifference > 0) ? 'OVERPAID_AMOUNT' : 'UNDERPAID_AMOUNT')"
            :value="(Math.abs(renewalDifference) / MILLION).toFixed(3)"
            :hint="changeDigitToViText((Math.abs(renewalDifference) / MILLION).toFixed(3))"
            borderbottom
            disabled
          />
        </b-col>
        <b-col xs="12" md="6" lg="4" class="input-wrapper" v-if="renew" v-show="renewalAmount >= 0">
          <text-input
            :label="$t('STORAGE_FEE_LATE')"
            v-model="storageFeeLate"
            :hint="changeDigitToViText(storageFeeLate)"
            borderbottom
          />
        </b-col>
      </b-row>
    </b-container>

    <b-container v-if="!!renew" v-show="renewalAmount > 0">
      <h2>{{ $t('TRANSACTIONS') }}</h2>

      <transaction-list
        v-if="!!formerContract.id"
        :contract-id="formerContract.id"
        :disbursed-on="formerContract.submittedOn"
        v-model="transactions"
        ref="transactionsRef"
      />
    </b-container>
  </div>
</template>

<script>
import { ref, computed, watch, watchEffect } from '@vue/composition-api';
import { createNamespacedHelpers } from 'vuex-composition-helpers';
import moment from 'moment';

import { changeDigitToViText } from '@/helpers';

import TextInput from '@/components/TextInput';
import SelectInput from '@/components/SelectInput';
import DateInput from '@/components/DateInput';
import TransactionList from '@/components/TransactionList';

import { DISPLAY_DATE_FORMAT, INTEREST_TYPE, DATE_FORMAT, MILLION, CASH_LOG_TYPES, TRANSACTION_STATUSES, TRANSACTION_ACTIONS } from '@/consts';

const { useState: useAppState } = createNamespacedHelpers('app');
const { useGetters: useUserGetters } = createNamespacedHelpers('user');

const MAX_INTEREST_RATE = 20;

export default {
  name: 'LoanForm',
  components: {
    TextInput,
    DateInput,
    SelectInput,
    TransactionList
  },
  props: {
    value: {
      type: Object,
      default: () => {}
    },
    asset: {
      type: Object,
      default: () => {}
    },
    locked: {
      type: Boolean,
      default: false
    },
    formerContract: {
      type: Object,
      default: null
    },
    review: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit, root }) {
    const { products: products_ } = useAppState(['products']);
    const { superuser } = useUserGetters(['superuser']);

    const transactions = ref([]);
    const sum = computed(() => transactions.value
      .filter((t) => ((t.action !== TRANSACTION_ACTIONS.WAIVE) && (t.status !== TRANSACTION_STATUSES.RETRACTED) && !t.parentId))
      .reduce((prev, t) => (parseFloat(t.amount || 0) + parseFloat(prev || 0)).toFixed(3), 0));
    const lastTransactionDate = computed(() => transactions.value.filter((t) => (t.status !== TRANSACTION_STATUSES.RETRACTED))[0]?.registeredOn);

    const products = computed(() => products_.value?.map((item) => {
      let rate = item.minInterestRate;
      if (!item.minInterestRate || !item.maxInterestRate) {
        rate = root.$t('FLEX_RATE');
      }

      return {
        text: `${item.name} | ${root.$t(item.interestType)} | ${item.loanTerm} ${root.$t('DAYS').toLowerCase()} | ${rate}`,
        value: item.id
      };
    }));

    const types = Object.keys(CASH_LOG_TYPES).map((key) => ({
      value: key,
      text: root.$t(key)
    }));

    const renew = computed(() => !!props.formerContract?.id);

    const review = computed(() => props.review);

    const id = ref(null);
    const finfluxId = ref(null);
    const product = ref(null);
    const approvedAmount = ref('');
    const storageFee = ref(0);
    const disbursedOn = ref(moment().format(DISPLAY_DATE_FORMAT));
    const interestRate = ref(0);
    const branchId = ref(null);
    const storageFeeLate = ref(0);

    const product_ = computed(() => products_.value.find((p) => p.id === product.value));
    const isAdvance = computed(() => (product_.value?.interestType === INTEREST_TYPE.ADVANCE));

    const formerContract = computed(() => props.formerContract);
    const formerProduct = computed(() => products_.value.find((p) => p.id === formerContract.value.productId));

    const finfluxInterestRate = computed(() => {
      if (!product_.value || !interestRate.value) return 0;

      let rate = interestRate.value;
      if (isAdvance.value) {
        rate = ((rate / (100 - rate)) * 100).toFixed(6);
      }

      return rate;
    });

    const principalAmount = computed(() => {
      const interest = (Math.ceil((((+approvedAmount.value * interestRate.value) / 100) * 1000).toFixed(3)) / 1000).toFixed(3);

      return (
        isAdvance.value
          ? (+approvedAmount.value - interest).toFixed(3)
          : (+approvedAmount.value).toFixed(3)
      );
    });
    const interestAmount = computed(() => (Math.round(((principalAmount.value * finfluxInterestRate.value) / 100) * 1000) / 1000).toFixed(3));
    const roundingFee = computed(() => (
      isAdvance.value
        ? Math.abs(approvedAmount.value - principalAmount.value - interestAmount.value).toFixed(3)
        : 0
    ));

    const earlyRenewalCumulativeDays = computed(() => {
      if (!formerContract.value) return 0;

      const maturityDate = moment(formerContract.value.maturesOn, DATE_FORMAT);
      const disbursementDate = moment(disbursedOn.value, DISPLAY_DATE_FORMAT);

      if (maturityDate <= disbursementDate) return 0;
      if (formerContract.value.approvedAmount < parseInt(parseFloat(approvedAmount.value).toFixed(3) * MILLION, 10)) return 0;

      let diff = maturityDate.diff(disbursementDate, 'days');
      if (Number.isNaN(diff)) diff = 0;

      return diff;
    });

    const maturesOn = ref(null);
    const canEditMaturityDate = ref(false);
    const maturesOnInput = ref(null);
    const transactionsRef = ref(null);

    const toggleCanEditMaturityDate = () => {
      canEditMaturityDate.value = !canEditMaturityDate.value;
    };

    const disbursementAmount = computed(() => {
      let amount = principalAmount.value - storageFee.value;
      amount = Math.round(amount * 1000);
      amount = (amount / 1000).toFixed(3);

      return amount;
    });

    const numberOfLateDays = computed(() => {
      if (!formerContract.value) return 0;
      if (!disbursedOn.value) return 0;

      const maturityDate = moment(formerContract.value.maturesOn);
      const date = moment(disbursedOn.value, DISPLAY_DATE_FORMAT);
      const diff = date.diff(maturityDate, 'days');

      return (diff < 0) ? 0 : diff;
    });

    const canEditOverdueFee = ref(false);
    const overdueFeePerDay = computed(() => {
      if (!formerContract.value) return 0;
      return (formerContract.value.interestAmount / formerProduct.value?.loanTerm / MILLION);
    });
    const overdueFeeSwap = ref(0);
    const overdueFee = computed(() => {
      const amount = Math.round(parseFloat(overdueFeeSwap.value) * 1000);
      return amount * 1000;
    });

    const toggleCanEditOverdueFee = () => {
      canEditOverdueFee.value = !canEditOverdueFee.value;
    };

    const renewalAmount = computed(() => {
      if (!formerContract.value) return 0;

      return (
        formerContract.value.principalAmount
      + formerContract.value.interestAmount
      + formerContract.value.roundingFee
      + overdueFee.value
      - formerContract.value.renewalDifference
      - formerContract.value.waivedAmount
      - (disbursementAmount.value ? Math.round(disbursementAmount.value * 1000) * 1000 : 0)
      );
    });

    const receivedAmount = ref(0);
    const receivedAmountInput = ref(null);

    const productInput = ref(null);
    const interestRateInput = ref(null);
    const approvedAmountInput = ref(null);
    const disbursedOnInput = ref(null);

    const renewalDifference = computed(() => ((Math.round(sum.value * 1000) * 1000) - renewalAmount.value));

    const wrappedContract = computed(() => {
      let receivedAmount_ = Math.floor(parseFloat(receivedAmount.value) * MILLION);
      if (renewalAmount.value < 0) {
        receivedAmount_ = 0;
      }

      let storageFeeLate_ = Math.floor(parseFloat(storageFeeLate.value) * MILLION);
      if (storageFeeLate.value < 0) {
        storageFeeLate_ = 0;
      }

      const storageFee_ = storageFee.value ? Math.floor(parseFloat(storageFee.value) * MILLION) : 0;

      let repaidAmount = receivedAmount_ - storageFee_;
      if (repaidAmount < 0) repaidAmount = 0;

      const maturityDate = maturesOn.value ? moment(maturesOn.value, review.value ? DATE_FORMAT : DISPLAY_DATE_FORMAT).format(DATE_FORMAT) : null;

      return {
        id: id.value || undefined,
        finfluxId: finfluxId.value || undefined,
        productId: product.value,
        interestRate: interestRate.value,
        storageFee: storageFee.value ? storageFee_ : undefined,
        roundingFee: roundingFee.value ? Math.floor(parseFloat(roundingFee.value) * MILLION) : 0,
        approvedAmount: approvedAmount.value ? Math.floor(parseFloat(approvedAmount.value) * MILLION) : null,
        disbursedOn: disbursedOn.value ? moment(disbursedOn.value, DISPLAY_DATE_FORMAT).format(DATE_FORMAT) : null,
        maturesOn: maturityDate,
        overridedMaturesOn: canEditMaturityDate.value ? maturityDate : null,
        interestAmount: interestAmount.value ? Math.floor(parseFloat(interestAmount.value) * MILLION) : null,
        disbursementAmount: disbursementAmount.value ? Math.floor(parseFloat(disbursementAmount.value) * MILLION) : null,
        isAdvance: isAdvance.value || false,
        repaidAmount: renew.value ? repaidAmount : undefined,
        receivedAmount: receivedAmount_,
        renewalAmount: renewalAmount.value,
        branchId: branchId.value,
        storageFeeLate: storageFeeLate_,
      };
    });

    const asset = computed(() => props.asset);

    const submit = () => {
      const inputs = [
        productInput.value,
        interestRateInput.value,
        approvedAmountInput.value,
        disbursedOnInput.value
      ];

      if (renew.value) {
        if (renewalAmount.value > 0) {
          inputs.push(receivedAmountInput.value);
        }
      }

      if (canEditMaturityDate.value) {
        inputs.push(maturesOnInput.value);
      }

      let valid = true;

      inputs.forEach((input) => {
        const v = input.validateInput();
        valid = valid && v;
      });

      if (renew.value) {
        const approved = Math.round(parseFloat(approvedAmount.value) * 1000) * 1000;
        if (approved <= formerContract.value.approvedAmount) {
          const transactionsValid = transactionsRef.value.validate();
          valid = valid && transactionsValid;
        }
      }

      return valid;
    };

    const validateApprovedAmount = (val) => {
      if (val && (parseFloat(val) * MILLION > parseFloat(asset.value.valuatedAmount))) {
        return 'APPROVED_AMOUNT_GREATER_THAN_VALUATED_AMOUNT';
      }

      return '';
    };

    const validateDisbursementAmount = (val) => {
      if (val && (parseFloat(val) < 0)) {
        return 'INVALID_DISBURSEMENT_AMOUNT';
      }

      return '';
    };

    const loadContractInfo = (val) => {
      if (val) {
        id.value = val.id || null;
        finfluxId.value = val.finfluxId || null;
        product.value = val.productId || null;
        interestRate.value = val.interestRate || 0;
        approvedAmount.value = val.approvedAmount ? (val.approvedAmount / MILLION).toFixed(3) : '';
        disbursedOn.value = val.disbursedOn || null;
        storageFee.value = val.storageFee ? (val.storageFee / MILLION).toFixed(3) : '';
        branchId.value = val.branchId;
        maturesOn.value = val.maturesOn || null;
      }
    };

    const validateRepaidAmount = (val) => { // eslint-disable-line
      // TODO: reconsider this condition
      const repaid = Math.abs(val * MILLION - renewalAmount.value);

      let renewal = renewalAmount.value;
      if (formerContract.value.renewalDifference > 0) {
        renewal += formerContract.value.renewalDifference;
      }

      if (repaid > renewal) {
        return 'INVALID_AMOUNT';
      }

      return '';
    };

    const validateDisbursementDate = (val) => {
      if (props.locked) return '';

      const date = moment(val, DISPLAY_DATE_FORMAT, true).startOf('day');

      if (date.isValid()) {
        const today = moment().startOf('day');
        const lim = today.clone().subtract(14, 'day');

        if (!superuser.value) {
          if (
            (today < date)
            || (date < lim)
          ) {
            return renew.value ? 'INVALID_RENEWAL_DATE' : 'INVALID_DISBURSEMENT_DATE';
          }
        }

        if (renew.value && formerContract.value) {
          const disbursementDate = moment(formerContract.value.submittedOn).startOf('day');
          if (disbursementDate > date) {
            return 'INVALID_RENEWAL_DATE';
          }
        }

        return '';
      }

      return 'INVALID_DATE_FORMAT';
    };

    const validateMaturityDate = (val) => {
      if (props.locked) return '';

      const date = moment(val, DISPLAY_DATE_FORMAT, true).startOf('day');
      const disbursementDate = moment(disbursedOn.value, DISPLAY_DATE_FORMAT).startOf('day');

      if (date.isValid()) {
        if (date <= disbursementDate) {
          return 'INVALID_MATURITY_DATE';
        }

        return '';
      }

      return 'INVALID_DATE_FORMAT';
    };

    watch(asset, () => {
      approvedAmountInput.value.validateInput();
    });

    watch(product_, (p) => {
      if (p) {
        if (!interestRate.value) {
          interestRate.value = p.minInterestRate;
        }
      } else {
        interestRate.value = 0;
      }
    });

    watchEffect(() => {
      if (!product_.value) return;

      if (!interestRate.value && (interestRate.value !== 0)) return;

      if (interestRate.value < (product_.value.minInterestRate || 0)) {
        interestRate.value = (product_.value.minInterestRate || 0);
      }

      if (interestRate.value > (product_.value.maxInterestRate || MAX_INTEREST_RATE)) {
        interestRate.value = (product_.value.maxInterestRate || MAX_INTEREST_RATE);
      }
    });

    watchEffect(() => {
      emit('input', wrappedContract.value);
    });

    watchEffect(() => {
      if (canEditMaturityDate.value) return;
      if (review.value) return;

      maturesOn.value = product_.value
        ? moment(disbursedOn.value, DISPLAY_DATE_FORMAT).add(product_.value.loanTerm + earlyRenewalCumulativeDays.value, 'days').format(DISPLAY_DATE_FORMAT)
        : null;
    });

    watchEffect(() => {
      if (canEditOverdueFee.value) return;

      const overduePerDay = Math.round(overdueFeePerDay.value * MILLION);
      let amount = overduePerDay * numberOfLateDays.value;
      amount = Math.round(amount / 1000);

      overdueFeeSwap.value = (amount / 1000).toFixed(3);
    });

    const emitRenewalInfo = () => {
      emit('renewalTransactionsUpdated', {
        overdueFee: overdueFee.value,
        overridedOverdueFee: canEditOverdueFee.value ? overdueFee.value : null,
        renewalDifference: renewalDifference.value,
        transactions: transactions.value.map((t) => ({
          ...t,
          registeredOn: moment(t.registeredOn, DISPLAY_DATE_FORMAT).format(DATE_FORMAT),
          amount: parseInt(Math.round(parseFloat(parseFloat(t.amount).toFixed(3)) * 1000) * 1000, 10)
        })),
        storageFeeLate: storageFeeLate.value
      });
    };

    const emitTransactions = () => {
      emit('transactions', transactions);
    };

    watch(sum, (val) => {
      receivedAmount.value = val;
      emitRenewalInfo();
    });

    watch(lastTransactionDate, (val) => {
      disbursedOn.value = val;
      emitRenewalInfo();
    });

    watch(transactions, () => {
      emitRenewalInfo();
      emitTransactions();
    });

    return {
      approvedAmount,
      principalAmount,
      interestAmount,
      disbursedOn,
      changeDigitToViText,
      product,
      products,
      product_,
      interestRate,
      storageFee,
      roundingFee,
      maturesOn,
      canEditMaturityDate,
      toggleCanEditMaturityDate,
      disbursementAmount,
      productInput,
      interestRateInput,
      approvedAmountInput,
      disbursedOnInput,
      submit,
      validateApprovedAmount,
      loadContractInfo,
      validateDisbursementAmount,
      numberOfLateDays,
      overdueFeeSwap,
      renewalAmount,
      MILLION,
      types,
      receivedAmount,
      receivedAmountInput,
      CASH_LOG_TYPES,
      validateRepaidAmount,
      renew,
      validateDisbursementDate,
      validateMaturityDate,
      earlyRenewalCumulativeDays,
      transactions,
      renewalDifference,
      maturesOnInput,
      toggleCanEditOverdueFee,
      canEditOverdueFee,
      transactionsRef,
      storageFeeLate
    };
  }
};
</script>

<style lang="scss">
@import "~@/assets/scss/colors";

#contract-editor-loan-form {
  .checkbox-wrapper {
    margin-bottom: 15px;
    display: inline-block;
    margin-right: 30px;

    .label {
      margin-left: 5px;
      vertical-align: top;
    }

    input {
      vertical-align: top;
      margin-top: 6px;
      cursor: pointer;
    }
  }
}
</style>
